import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

     const video = this.querySelector("video[autoplay]");

  if (video) {
    requestAnimationFrame(() => {
      video.muted = true;
      video.play().catch(() => {});
    });
  }

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
#handleVariantUpdate = (event) => {
  console.log("Variant Updated");

  const source = event.detail.data.html;

  if (!source) {
    console.log("No source");
    return;
  }

  const newMediaGallery = source.querySelector("media-gallery");

  if (!newMediaGallery) {
    console.log("No media gallery");
    return;
  }

  console.log("Media gallery found");

  this.replaceWith(newMediaGallery);

  requestAnimationFrame(() => {
    console.log("After replace");

    const deferred = newMediaGallery.querySelector("deferred-media");

    console.log("Deferred:", deferred);

    if (!deferred) return;

    const btn = deferred.querySelector(".deferred-media__poster-button");

    console.log("Button:", btn);

    btn?.click();

    setTimeout(() => {
      const video = deferred.querySelector("video");

      console.log("Video:", video);

      if (video) {
        console.log("Paused:", video.paused);
        console.log("ReadyState:", video.readyState);

        video.play().then(() => {
          console.log("Video Playing");
        }).catch(err => {
          console.error("Play Error:", err);
        });
      }
    }, 300);
  });
};

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
